import { createSignal, type Component, For } from "solid-js";
import { PanelLayout, type SubTab, type PanelStatus } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { useToolClient } from "@/shared/tool-client";
import { useConfirm } from "@/shared/confirm-dialog";
import { useWs } from "@/shared/ws";
import { registerPanel } from "@/panel-registry";

interface Printer {
  Name?: string;
  DriverName?: string;
  PortName?: string;
  Shared?: boolean;
  Published?: boolean;
  PrinterStatus?: string;
  Location?: string;
}

interface PrintJob {
  Id?: number;
  DocumentName?: string;
  UserName?: string;
  SubmittedTime?: string;
  JobStatus?: string;
  Size?: number;
  PagesPrinted?: number;
  TotalPages?: number;
}

interface ListResult<T> {
  rows: T[];
}

type PrinterActionId = "pause-printer" | "resume-printer";
type JobActionId = "pause-job" | "resume-job" | "cancel-job";

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Oui" : "Non";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

const PrintPanel: Component = () => {
  const ws = useWs();
  const confirm = useConfirm();
  const tools = useToolClient();
  const [selectedPrinter, setSelectedPrinter] = createSignal("");
  const [actionStatus, setActionStatus] = createSignal<PanelStatus | null>(null);

  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  const printersQuery = createToolQuery<ListResult<Printer>>(() => ({
    tool: "print_printers_list",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const jobsQuery = createToolQuery<ListResult<PrintJob>>(() => ({
    tool: "print_jobs_list",
    params: { printerName: selectedPrinter() },
    enabled: enabled() && selectedPrinter() !== "",
    refetchInterval: 0,
  }));

  async function runPrinterAction(
    printerName: string,
    actionId: PrinterActionId,
    label: string,
    level: "info" | "warn" | "danger",
  ): Promise<void> {
    if (!printerName) return;
    const ok = await confirm.confirm({
      title: `${label} — ${printerName}`,
      body: `Confirmer "${label}" sur l'imprimante "${printerName}" ?`,
      level,
      confirmLabel: label,
    });
    if (!ok) return;
    setActionStatus({ text: `Execution ${label} sur ${printerName}...`, level: "info" });
    try {
      await tools.call(
        "print_action",
        { printerName, action: actionId },
        { userConfirmedAt: new Date().toISOString() },
      );
      setActionStatus({ text: `Action ${label} reussie.`, level: "info" });
      await printersQuery.refetch();
    } catch (err) {
      setActionStatus({
        text: `Erreur: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  async function runJobAction(
    job: PrintJob,
    actionId: JobActionId,
    label: string,
    level: "info" | "warn" | "danger",
  ): Promise<void> {
    const printerName = selectedPrinter();
    if (!printerName || job.Id == null) return;
    const ok = await confirm.confirm({
      title: `${label} job ${job.Id}`,
      body: `Confirmer "${label}" sur le job ${job.Id} (${job.DocumentName ?? "?"}) de "${printerName}" ?`,
      level,
      confirmLabel: label,
      entityName: level === "danger" ? String(job.Id) : "",
    });
    if (!ok) return;
    setActionStatus({ text: `Execution ${label} sur job ${job.Id}...`, level: "info" });
    try {
      await tools.call(
        "print_action",
        { printerName, jobId: Number(job.Id), action: actionId },
        { userConfirmedAt: new Date().toISOString() },
      );
      setActionStatus({ text: `Action ${label} reussie.`, level: "info" });
      await jobsQuery.refetch();
    } catch (err) {
      setActionStatus({
        text: `Erreur: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  const printerColumns: Column<Printer>[] = [
    { accessorKey: "Name", header: "Name" },
    { accessorKey: "DriverName", header: "DriverName" },
    { accessorKey: "PortName", header: "PortName" },
    {
      accessorKey: "Shared",
      header: "Shared",
      cell: (info) => formatCell(info.getValue()),
    },
    {
      accessorKey: "Published",
      header: "Published",
      cell: (info) => formatCell(info.getValue()),
    },
    { accessorKey: "PrinterStatus", header: "PrinterStatus" },
    { accessorKey: "Location", header: "Location" },
  ];

  const jobColumns: Column<PrintJob>[] = [
    { accessorKey: "Id", header: "Id" },
    { accessorKey: "DocumentName", header: "DocumentName" },
    { accessorKey: "UserName", header: "UserName" },
    {
      accessorKey: "SubmittedTime",
      header: "SubmittedTime",
      cell: (info) => formatCell(info.getValue()),
    },
    { accessorKey: "JobStatus", header: "JobStatus" },
    { accessorKey: "Size", header: "Size" },
    { accessorKey: "PagesPrinted", header: "PagesPrinted" },
    { accessorKey: "TotalPages", header: "TotalPages" },
  ];

  const subtabs: SubTab[] = [
    {
      id: "printers",
      label: "Imprimantes",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">
            <button type="button" class="btn btn--xs btn--info" onClick={() => printersQuery.refetch()}>
              Rafraichir
            </button>
          </div>
          <DataTable<Printer>
            rows={printersQuery.data?.rows ?? []}
            columns={printerColumns}
            rowActions={(r) => {
              const isPaused = String(r.PrinterStatus ?? "")
                .toLowerCase()
                .includes("paused");
              return (
                <>
                  <button
                    type="button"
                    class="btn btn--xs btn--warn"
                    disabled={isPaused}
                    onClick={() => runPrinterAction(r.Name ?? "", "pause-printer", "Pause", "warn")}
                  >
                    Pause
                  </button>
                  <button
                    type="button"
                    class="btn btn--xs btn--ghost"
                    disabled={!isPaused}
                    onClick={() => runPrinterAction(r.Name ?? "", "resume-printer", "Resume", "info")}
                  >
                    Resume
                  </button>
                </>
              );
            }}
          />
        </>
      ),
    },
    {
      id: "jobs",
      label: "Files d'attente",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">
            <span class="panel-toolbar-label">Imprimante:</span>
            <select
              class="panel-filter-input"
              value={selectedPrinter()}
              onChange={(e) => setSelectedPrinter(e.currentTarget.value)}
            >
              <option value="">-- choisir une imprimante --</option>
              <For each={printersQuery.data?.rows ?? []}>
                {(p) => <option value={p.Name ?? ""}>{p.Name ?? ""}</option>}
              </For>
            </select>
            <button
              type="button"
              class="btn btn--xs btn--info"
              disabled={!selectedPrinter()}
              onClick={() => jobsQuery.refetch()}
            >
              Rafraichir
            </button>
          </div>
          <DataTable<PrintJob>
            rows={jobsQuery.data?.rows ?? []}
            columns={jobColumns}
            rowActions={(r) => {
              const isPaused = String(r.JobStatus ?? "")
                .toLowerCase()
                .includes("paused");
              return (
                <>
                  <button
                    type="button"
                    class="btn btn--xs btn--warn"
                    disabled={isPaused}
                    onClick={() => runJobAction(r, "pause-job", "Pause", "warn")}
                  >
                    Pause
                  </button>
                  <button
                    type="button"
                    class="btn btn--xs btn--ghost"
                    disabled={!isPaused}
                    onClick={() => runJobAction(r, "resume-job", "Resume", "info")}
                  >
                    Resume
                  </button>
                  <button
                    type="button"
                    class="btn btn--xs btn--danger"
                    onClick={() => runJobAction(r, "cancel-job", "Cancel", "danger")}
                  >
                    Cancel
                  </button>
                </>
              );
            }}
          />
        </>
      ),
    },
  ];

  const status = (): PanelStatus | undefined => {
    if (actionStatus()) return actionStatus() ?? undefined;
    if (!ws.state.nativeAgentOnline) return { text: "Module natif requis.", level: "warn" };
    if (!ws.state.supportApproved) return { text: "Validation client requise.", level: "warn" };
    if (printersQuery.isFetching || jobsQuery.isFetching) return { text: "Chargement...", level: "info" };
    return undefined;
  };

  return <PanelLayout title="Imprimantes" subtabs={subtabs} status={status()} />;
};

registerPanel("print", PrintPanel);
export default PrintPanel;
